import React from 'react';
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
  Footer,
  FooterRow,
  FooterCell
} from '@table-library/react-table-library/table';
import { useTheme } from '@table-library/react-table-library/theme';
import { getTheme } from '@table-library/react-table-library/baseline';
import { usePagination } from '@table-library/react-table-library/pagination';
import { useSort, HeaderCellSort } from '@table-library/react-table-library/sort';
import { isReactText, lang, remCss } from 'src/helpers';
import { MCButton, MCText, Spinner, View } from 'src/components/basic';
import LANG_CONST from 'src/helpers/lang/const';
import { EllipsisTooltip } from 'components/basic/Tooltip';

export const convertColumnsForSmallWidth = (columns) => {
  const col = {
    renderCell: (item) => {
      return (
        <View style={{ gap: remCss(15) }}>
          {columns.map((col, i) => (
            <View style={{ flexDirection: 'row', gap: remCss(15) }} key={i}>
              {typeof col.label === 'string' ? (
                <MCText flex label={col.label} weight={500} />
              ) : (
                col.label
              )}
              {col.renderCell(item)}
            </View>
          ))}
        </View>
      );
    }
  };
  const haveFooter = columns.find((column) => !!column.footer);
  if (haveFooter) {
    col.footer = () => {
      return (
        <View style={{ gap: remCss(15) }}>
          {columns
            .filter((col) => !!col.footer)
            .map((col, i) => (
              <View style={{ flexDirection: 'row', gap: remCss(15) }} key={i}>
                {typeof col.label === 'string' ? (
                  <MCText flex label={col.label} weight={500} />
                ) : (
                  col.label
                )}
                {col.footer()}
              </View>
            ))}
        </View>
      );
    };
  }
  return [col];
};

const ENABLE_SORT = false;

export const TableLibrary = ({
  data,
  total,
  onSetPage,
  onSetSize,
  onSortChange,
  columns,
  limit,
  loading,
  noResults,
  searchQuery,
  sortBy,
  selectedPage = 0,
  template = 0,
  className = '',
  renderExtend,
  renderPrefixExtend,
  footer,
  showHeader = true,
  showPagination = true,
  enableHeaderTooltip = false,
  emptyText = null,
  onRowClick,
  topRowExtend,
  otherProps = {}
}) => {
  const pageCount = Math.ceil(total / limit);
  const pagingList = [];

  const haveFooter = columns.find((column) => !!column.footer);

  const tableData = {
    nodes: data,
    pageInfo: {
      total
    }
  };

  const templateColumns = columns
    .map((col) => `minmax(${col.minWidth || 0}px, ${col.width || '1fr'})`)
    .join(' ');

  const theme = useTheme({
    ...getTheme(),
    Table: `
        --data-table-library_grid-template-columns: ${templateColumns};
        z-index: 0;
      `,
    Row: `
        &:not(:last-of-type) .td {
          border-color: transparent;
        }
      `
  });
  const pagination = usePagination(
    tableData,
    {
      state: {
        page: selectedPage,
        size: limit
      },
      onChange: (action, state) => {
        if (action.type === 'SET') {
          if (action.payload.size) {
            onSetSize && onSetSize(state.size);
          } else {
            onSetPage && onSetPage(state.page);
          }
        }
      }
    },
    {
      isServer: true
    }
  );

  const sort = useSort(
    data,
    {
      state: { sortKey: sortBy?.sortKey, reverse: sortBy?.desc },
      onChange: (action, state) =>
        onSortChange && onSortChange({ sortKey: state.sortKey, desc: state.reverse })
    },
    {
      sortIcon: {
        iconDefault: null
      },
      isServer: true
    }
  );
  const canPreviousPage = () => {
    return pagination.state.page > 0;
  };

  const canNextPage = () => {
    const pageCount = Math.floor(total / limit);
    return pagination.state.page < pageCount;
  };

  for (let i = 0; i < pageCount; i++) {
    if ((selectedPage - 3 <= i && selectedPage + 3 >= i) || i < 3 || i > pageCount - 3)
      pagingList.push(
        <MCButton
          key={i}
          primary={pagination.state.page === i}
          text={pagination.state.page !== i}
          dark={pagination.state.page === i}
          outline={pagination.state.page !== i}
          disabled={pagination.state.page === i}
          radius
          small
          style={{ minWidth: remCss(34) }}
          onClick={() => {
            pagination.fns.onSetPage(i);
          }}
          className='page-link'
        >
          {i + 1}
        </MCButton>
      );
    else if (pagingList[pagingList.length - 1] !== '...') {
      pagingList.push('...');
    }
  }

  return (
    <div className={`mc-table-container template-${template} ${className}`}>
      <div className={`mc-table`}>
        <Table
          data={tableData}
          layout={{ fixedHeader: true, custom: true }}
          theme={theme}
          pagination={pagination}
          sort={sort}
        >
          {(tableList) => (
            <>
              {showHeader && (
                <Header>
                  <HeaderRow>
                    {columns.map((col, index) => {
                      const headerProps = otherProps?.header ? otherProps.header(col, index) : {};
                      const content = isReactText(col.label) ? lang.t(col.label) : col.label;
                      return (col.sort && ENABLE_SORT) ||
                        (sortBy?.sortKey && col.sort?.sortKey === sortBy?.sortKey) ? (
                        <HeaderCellSort
                          key={index}
                          resize={col.resize}
                          style={col.cellStyles}
                          sortKey={col.sort?.sortKey}
                          {...headerProps}
                        >
                          {content}
                        </HeaderCellSort>
                      ) : (
                        <HeaderCell
                          key={index}
                          resize={col.resize}
                          style={col.cellStyles}
                          {...headerProps}
                        >
                          {col.headerTooltip || enableHeaderTooltip ? (
                            <EllipsisTooltip text={col.headerTooltip || content}>
                              {content}
                            </EllipsisTooltip>
                          ) : (
                            content
                          )}
                        </HeaderCell>
                      );
                    })}
                  </HeaderRow>
                </Header>
              )}

              <Body>
                {topRowExtend && topRowExtend()}
                {tableList.map((item, rowIndex) => {
                  const ex = renderExtend && renderExtend(item);
                  const prefixEx = renderPrefixExtend && renderPrefixExtend(item);
                  const rowProps = otherProps?.row ? otherProps.row(item, rowIndex) : {};
                  const key = rowProps?.key || item?.id || rowIndex;
                  return (
                    <React.Fragment key={key}>
                      {prefixEx}
                      <Row
                        item={item}
                        data-testid='table-row'
                        onClick={onRowClick}
                        style={onRowClick ? { cursor: 'pointer' } : { borderColor: 'red' }}
                        {...rowProps}
                      >
                        {columns.map((col, colIndex) => (
                          <Cell
                            key={rowIndex + '-' + colIndex}
                            style={col.cellStyles}
                            {...(col.cellProps || {})}
                            {...(otherProps?.cell
                              ? otherProps.cell(col, colIndex, item, rowIndex)
                              : {})}
                          >
                            {col.renderCell(item, rowIndex)}
                          </Cell>
                        ))}
                      </Row>
                      {ex && (
                        <tr style={{ display: 'flex', gridColumn: '1 / -1' }}>
                          <td style={{ flex: '1' }}>{ex}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </Body>
              {haveFooter && (
                <Footer>
                  <FooterRow>
                    {columns.map((col, colIndex) => (
                      <FooterCell
                        key={colIndex}
                        // style={col.cellStyles}
                        // {...(col.cellProps || {})}
                      >
                        {col.footer && col.footer(col, colIndex)}
                      </FooterCell>
                    ))}
                  </FooterRow>
                </Footer>
              )}
            </>
          )}
        </Table>
      </div>
      {noResults && !searchQuery && (
        <div className={'mc-notice'}>{lang.t(emptyText || LANG_CONST.text.no_results_yet)}</div>
      )}
      {noResults && searchQuery && (
        <div className={'mc-notice'}>{lang.t(LANG_CONST.text.nothing_found)}</div>
      )}
      {loading && (
        <div className={'mc-notice'}>
          <Spinner />
        </div>
      )}
      {footer || (showPagination && !!total && <div className={'mc-grow'}></div>)}
      {showPagination && !!total && (
        <div className='mc-table-footer'>
          <div className={'mc-grow'}></div>
          <div className='mc-flex-10'>
            <MCButton
              tiny
              text
              onClick={() => pagination.fns.onSetPage(pagination.state.page - 1)}
              disabled={!canPreviousPage()}
            >
              <span className={'mc-text-418'}>{lang.t(LANG_CONST.buttons.previous)}</span>
            </MCButton>
            {pagingList}
            <MCButton
              tiny
              text
              onClick={() => pagination.fns.onSetPage(pagination.state.page + 1)}
              disabled={!canNextPage()}
            >
              <span className={'mc-text-418'}>{lang.t(LANG_CONST.buttons.next)}</span>
            </MCButton>
          </div>
        </div>
      )}
    </div>
  );
};
